import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import MDTypography from "../../../materialDashboard/components/MDTypography";
import MDButton from "../../../materialDashboard/components/MDButton";
import MDBox from "../../../materialDashboard/components/MDBox";
import {useBulkUpdateModels1, useDeleteModel1} from "../query";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import ErrorIcon from "@mui/icons-material/Error";
import Stack from "@mui/material/Stack";
import {errorParser} from "../../../utils/errors/errorParser";

const BulkUpdateDialog = ({open, onClose, updates, deletes, onCompleted}) => {
    const {t} = useTranslation(["models1", "components", "errors"]);
    const [status, setStatus] = useState("idle"); // idle, processing, completed
    const [results, setResults] = useState({
        updated: {success: 0, total: 0, errors: {}},
        deleted: {success: 0, total: 0, errors: {}}
    });

    const bulkUpdate = useBulkUpdateModels1();
    const deleteModel = useDeleteModel1();

    const handleConfirm = async () => {
        setStatus("processing");
        
        let updateResult = {success: 0, total: Object.keys(updates).length, errors: {}};
        let deleteResult = {success: 0, total: deletes.length, errors: {}};

        const addError = (res, error) => {

            const parsed = errorParser(error, t);
            const messages = parsed.messages || [];
            console.log(error, parsed)
            if (messages.length === 0) {
                const generic = t("multipleEditing.error.generic");
                res.errors[generic] = (res.errors[generic] || 0) + 1;
            } else {
                messages.forEach(msg => {
                    res.errors[msg] = (res.errors[msg] || 0) + 1;
                });
            }
        };

        // 1. Bulk Update
        if (updateResult.total > 0) {
            try {
                await bulkUpdate.mutateAsync(Object.values(updates));
                updateResult.success = updateResult.total;
            } catch (e) {
                addError(updateResult, e);
            }
        }

        // 2. Sequential Deletes
        if (deleteResult.total > 0) {
            for (const id of deletes) {
                try {
                    await deleteModel.mutateAsync({id, showNotification: false});
                    deleteResult.success++;
                } catch (e) {
                    addError(deleteResult, e);
                }
                await new Promise(resolve => setTimeout(resolve, 100));
            }
        }

        setResults({updated: updateResult, deleted: deleteResult});
        setStatus("completed");
        if (onCompleted) onCompleted();
    };

    const isProcessing = status === "processing";
    const isCompleted = status === "completed";

    const renderErrors = (errors) => {
        return Object.entries(errors).map(([msg, count]) => (
            <MDTypography key={msg} variant="caption" color="error" display="block" sx={{ml: 2}}>
                - {msg} ({count})
            </MDTypography>
        ));
    };

    const getStatusIcon = (res) => {
        const errorCount = Object.keys(res.errors).length;
        if (errorCount === 0) {
            return <CheckCircleIcon color="success" fontSize="small"/>;
        }
        if (res.success > 0) {
            return <WarningIcon color="warning" fontSize="small"/>;
        }
        return <ErrorIcon color="error" fontSize="small"/>;
    };

    return (
        <Dialog open={open} onClose={isCompleted ? onClose : undefined} maxWidth="sm" fullWidth>
            <DialogTitle>
                {isCompleted ? t("multipleEditing.dialog.resultsTitle") : t("multipleEditing.confirmationDialog.title")}
            </DialogTitle>
            <DialogContent>
                <MDBox py={1}>
                    {status === "idle" && (
                        <>
                            {Object.keys(updates).length > 0 && (
                                <MDTypography variant="body2">
                                    {t("multipleEditing.confirmationDialog.updatesCount", {count: Object.keys(updates).length})}
                                </MDTypography>
                            )}
                            {deletes.length > 0 && (
                                <MDTypography variant="body2">
                                    {t("multipleEditing.confirmationDialog.deletesCount", {count: deletes.length})}
                                </MDTypography>
                            )}
                            <MDTypography variant="body2" mt={2} fontWeight="medium">
                                {t("multipleEditing.confirmationDialog.finalConfirmation")}
                            </MDTypography>
                        </>
                    )}
                    {isProcessing && (
                        <MDTypography variant="body2">
                            {t("multipleEditing.dialog.processing")}
                        </MDTypography>
                    )}
                    {isCompleted && (
                        <MDBox>
                            {results.updated.total > 0 && (
                                <MDBox mb={results.deleted.total > 0 ? 2 : 0}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        {getStatusIcon(results.updated)}
                                        <MDTypography variant="body2" color={Object.keys(results.updated.errors).length > 0 && results.updated.success === 0 ? "error" : "text"}>
                                            {t("multipleEditing.dialog.updatedSummary", {
                                                success: results.updated.success,
                                                total: results.updated.total
                                            })}
                                        </MDTypography>
                                    </Stack>
                                    {renderErrors(results.updated.errors)}
                                </MDBox>
                            )}
                            {results.deleted.total > 0 && (
                                <MDBox>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        {getStatusIcon(results.deleted)}
                                        <MDTypography variant="body2" color={Object.keys(results.deleted.errors).length > 0 && results.deleted.success === 0 ? "error" : "text"}>
                                            {t("multipleEditing.dialog.deletedSummary", {
                                                success: results.deleted.success,
                                                total: results.deleted.total
                                            })}
                                        </MDTypography>
                                    </Stack>
                                    {renderErrors(results.deleted.errors)}
                                </MDBox>
                            )}
                        </MDBox>
                    )}
                </MDBox>
            </DialogContent>
            <DialogActions>
                {!isCompleted && (
                    <MDButton onClick={onClose} disabled={isProcessing} color="secondary">
                        {t("multipleEditing.cancel")}
                    </MDButton>
                )}
                {!isCompleted ? (
                    <MDButton 
                        onClick={handleConfirm} 
                        color="info" 
                        variant="gradient" 
                        disabled={isProcessing}
                    >
                        {t("multipleEditing.submit")}
                    </MDButton>
                ) : (
                    <MDButton onClick={onClose} color="info" variant="gradient">
                        {t("multipleEditing.close")}
                    </MDButton>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default BulkUpdateDialog;
